import { createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { PlaybackMonitoringModels } from '@models/playback-monitoring.models';
import { PlaybackMonitoringActions } from '@states/playback-monitoring/playback-monitoring.action-types';
import { Dictionary } from '@ngrx/entity/src/models';
import {
  cleanPlaybackListFail,
  cleanPlaybackListSuccess,
  getListForCleaningFail,
  getListForCleaningSuccess, skipSuccess, startCleanPlaybackList,
  stopShutdownLoading,
} from '@states/playback-monitoring/playback-monitoring.actions';

export declare interface PlaybackMonitoringState extends EntityState<PlaybackMonitoringModels.PlaybackListItem> {
  playbackDevices: PlaybackMonitoringModels.PlaybackDevice[];
  initialLoading: boolean;
  isDeploying: boolean;
  shutDownIds: Dictionary<boolean>;
  cleanListLoading: boolean;
  cleanList: PlaybackMonitoringModels.PlaybackListItem[];
  isCleaningLoading: boolean;
}

export const playbackMonitoringAdapter: EntityAdapter<PlaybackMonitoringModels.PlaybackListItem> = createEntityAdapter<PlaybackMonitoringModels.PlaybackListItem>({
  selectId: (playback: PlaybackMonitoringModels.PlaybackListItem) => playback.serverName,
});

const initialState: PlaybackMonitoringState = playbackMonitoringAdapter.getInitialState({
  playbackDevices: null,
  initialLoading: true,
  isDeploying: false,
  shutDownIds: {},
  cleanListLoading: false,
  cleanList: [],
  isCleaningLoading: false,
});

export const playbackMonitoringStateReducer = createReducer(
  initialState,
  on(PlaybackMonitoringActions.resetToInitialState, state => {
    return {
      ...initialState,
    };
  }),
  on(PlaybackMonitoringActions.getPlaybackMonitoringSuccess, (state, { playbacks, playbackDevices }) => {
    return playbackMonitoringAdapter.setAll(playbacks, { ...state, playbackDevices });
  }),
  on(PlaybackMonitoringActions.setInitialLoader, (state, { loading }) => {
    return {
      ...state,
      initialLoading: loading,
    };
  }),
  on(PlaybackMonitoringActions.setIsDeploying, (state, { isDeploying }) => {
    return {
      ...state,
      isDeploying,
    };
  }),
  on(PlaybackMonitoringActions.deploy, (state) => {
    return {
      ...state,
      isDeploying: true,
    };
  }),
  on(PlaybackMonitoringActions.shutdown, (state, { vmId }) => {
    const shutDownIds = { ...state.shutDownIds };
    shutDownIds[vmId] = true;
    return {
      ...state,
      shutDownIds,
    };
  }),
  on(PlaybackMonitoringActions.stopShutdownLoading, (state, { vmId }) => {
    const shutDownIds = { ...state.shutDownIds };
    delete shutDownIds[vmId];
    return {
      ...state,
      shutDownIds,
    };
  }),
  on(PlaybackMonitoringActions.getListForCleaning, (state) => {
    return {
      ...state,
      cleanListLoading: true,
    };
  }),
  on(PlaybackMonitoringActions.getListForCleaningFail, (state) => {
    return {
      ...state,
      cleanListLoading: false,
    };
  }),
  on(PlaybackMonitoringActions.getListForCleaningSuccess, (state, { list }) => {
    return {
      ...state,
      cleanList: list,
      cleanListLoading: false,
    };
  }),
  on(PlaybackMonitoringActions.startCleanPlaybackList, (state) => {
    return {
      ...state,
      isCleaningLoading: true,
    };
  }),
  on(PlaybackMonitoringActions.cleanPlaybackListSuccess, (state) => {
    return {
      ...state,
      cleanList: initialState.cleanList,
      isCleaningLoading: false,
    };
  }),
  on(PlaybackMonitoringActions.cleanPlaybackListFail, (state) => {
    return {
      ...state,
      isCleaningLoading: false,
    };
  }),
  on(PlaybackMonitoringActions.skipSuccess, (state, { vmId, skip }) => {
    const db = state.entities[vmId].db;
    return playbackMonitoringAdapter.updateOne({
      id: vmId, changes: {
        db: {
          ...db,
          skip,
        },
      },
    }, state);
  }),
);
